import { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../utils/axios';
import {
  GlobeAltIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { AnalyticsDashboard, NginxTrafficHeader } from '../nginx-analytics';

// Status badge colors
const STATUS_COLORS = {
  '2xx': { bg: 'bg-green-100', text: 'text-green-700' },
  '3xx': { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  '4xx': { bg: 'bg-orange-100', text: 'text-orange-700' },
  '5xx': { bg: 'bg-red-100', text: 'text-red-700' },
};

// Project badge colors
const PROJECT_COLORS = {
  'iot-dashboard': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'IoT' },
  'user-dashboard': { bg: 'bg-green-100', text: 'text-green-700', label: 'User' },
  'marketing': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Mkt' },
};

// Country flag emoji helper
function getCountryFlag(countryCode) {
  if (!countryCode) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

// Format timestamp
function formatTime(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

// Format date
function formatDate(timestamp) {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Traffic Table Row
function TrafficRow({ log, onClick }) {
  const projectColor = PROJECT_COLORS[log.project] || { bg: 'bg-gray-100', text: 'text-gray-700', label: '?' };
  const statusColor = STATUS_COLORS[log.request?.statusCategory] || { bg: 'bg-gray-100', text: 'text-gray-700' };

  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer border-b border-gray-100"
      onClick={() => onClick?.(log)}
    >
      <td className="px-4 py-3 text-sm">
        <div className="text-gray-900 font-mono">{formatTime(log.timestamp)}</div>
        <div className="text-xs text-gray-400">{formatDate(log.timestamp)}</div>
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded text-xs font-medium ${projectColor.bg} ${projectColor.text}`}>
          {projectColor.label}
        </span>
      </td>
      <td className="px-4 py-3 text-sm">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
          log.request?.method === 'GET' ? 'bg-blue-100 text-blue-700' :
          log.request?.method === 'POST' ? 'bg-green-100 text-green-700' :
          log.request?.method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
          log.request?.method === 'DELETE' ? 'bg-red-100 text-red-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {log.request?.method || '-'}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm text-gray-900 font-mono max-w-[300px] truncate" title={log.request?.uri}>
          {log.request?.uri || '/'}
        </div>
        {log.request?.query && (
          <div className="text-xs text-gray-400 truncate max-w-[300px]" title={log.request.query}>
            ?{log.request.query}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
          {log.request?.status || '-'}
        </span>
      </td>
      <td className="px-4 py-3 text-sm">
        {log.geo?.country ? (
          <div className="flex items-center gap-1">
            <span>{getCountryFlag(log.geo.country)}</span>
            <span className="text-gray-600">{log.geo.city || log.geo.country}</span>
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {log.flags?.isBot && (
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">Bot</span>
          )}
          <span className="text-xs text-gray-500 truncate max-w-[100px]" title={log.device?.browser}>
            {log.device?.browser || '-'}
          </span>
        </div>
      </td>
    </tr>
  );
}

// Log Detail Modal
function TrafficDetailModal({ log, isOpen, onClose }) {
  if (!isOpen || !log) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-auto m-4" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Traffic Details</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Request Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Request</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Timestamp</span>
                <span className="font-mono">{new Date(log.timestamp).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Project</span>
                <span>{log.project}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Method</span>
                <span className="font-mono">{log.request?.method}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">URI</span>
                <span className="font-mono text-right max-w-[300px] truncate">{log.request?.uri}</span>
              </div>
              {log.request?.query && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Query</span>
                  <span className="font-mono text-right max-w-[300px] truncate">{log.request.query}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  STATUS_COLORS[log.request?.statusCategory]?.bg
                } ${STATUS_COLORS[log.request?.statusCategory]?.text}`}>
                  {log.request?.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Response Time</span>
                <span>{log.request?.responseTime ? `${log.request.responseTime}s` : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Bytes</span>
                <span>{log.request?.bytes?.toLocaleString() || 0}</span>
              </div>
            </div>
          </div>

          {/* Client Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Client</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">IP Address</span>
                <span className="font-mono">{log.actor?.ip}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Location</span>
                <span>
                  {log.geo?.country && getCountryFlag(log.geo.country)} {log.geo?.city || '-'}, {log.geo?.country || '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Device</span>
                <span>{log.device?.type || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Browser</span>
                <span>{log.device?.browser} {log.device?.browserVersion}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">OS</span>
                <span>{log.device?.os} {log.device?.osVersion}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Bot</span>
                <span>{log.flags?.isBot ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          {/* User Agent */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">User Agent</h4>
            <p className="text-xs text-gray-600 font-mono break-all">{log.actor?.userAgent || '-'}</p>
          </div>

          {log.request?.referer && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Referer</h4>
              <p className="text-sm text-gray-600 break-all">{log.request.referer}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main NginxTrafficView Component
export default function NginxTrafficView() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // View mode: 'charts' or 'table'
  const [viewMode, setViewMode] = useState('charts');

  // Filters
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [timeRange, setTimeRange] = useState('24h');
  const [customRange, setCustomRange] = useState(null);

  // Modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Last updated timestamp
  const [lastUpdated, setLastUpdated] = useState(null);

  // Calculate time range dates
  const getTimeRange = useCallback(() => {
    // If custom range is set, use it
    if (customRange?.start && customRange?.end) {
      return { startDate: customRange.start, endDate: customRange.end };
    }

    const now = new Date();
    const ranges = {
      '1h': 1 * 60 * 60 * 1000,
      '6h': 6 * 60 * 60 * 1000,
      '24h': 24 * 60 * 60 * 1000,
      '2d': 2 * 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '14d': 14 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000,
    };
    const startDate = new Date(now.getTime() - (ranges[timeRange] || ranges['24h']));
    return { startDate: startDate.toISOString(), endDate: now.toISOString() };
  }, [timeRange, customRange]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getTimeRange();
      const params = new URLSearchParams();

      if (selectedProject !== 'all') params.append('project', selectedProject);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      if (searchTerm) params.append('search', searchTerm);
      params.append('startDate', startDate);
      params.append('endDate', endDate);
      params.append('limit', '200');

      const response = await axiosInstance.get(`/logs/nginx?${params.toString()}`);
      setLogs(response.data.logs || []);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [selectedProject, selectedStatus, searchTerm, getTimeRange]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const { startDate, endDate } = getTimeRange();
      const params = new URLSearchParams();
      params.append('startDate', startDate);
      params.append('endDate', endDate);

      const response = await axiosInstance.get(`/logs/nginx/stats?${params.toString()}`);
      setStats(response.data.stats || null);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [getTimeRange]);

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/logs/nginx/projects');
      setProjects(response.data.projects || []);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchLogs();
      fetchStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs, fetchStats]);

  // Handle row click
  const handleRowClick = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!logs.length) return;

    const headers = ['Timestamp', 'Project', 'Method', 'URI', 'Status', 'IP', 'Country', 'City', 'Browser', 'Bot'];
    const rows = logs.map(log => [
      new Date(log.timestamp).toISOString(),
      log.project,
      log.request?.method,
      log.request?.uri,
      log.request?.status,
      log.actor?.ip,
      log.geo?.country,
      log.geo?.city,
      log.device?.browser,
      log.flags?.isBot ? 'Yes' : 'No'
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `nginx-traffic-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchLogs();
    fetchStats();
  };

  // Handle view mode change
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  return (
    <div className="space-y-6">
      {/* Unified Header */}
      <NginxTrafficHeader
        viewMode={viewMode}
        onToggleView={handleViewModeChange}
        projects={projects}
        selectedProject={selectedProject}
        onProjectChange={setSelectedProject}
        projectCounts={stats?.byProject || {}}
        totalCount={stats?.total || 0}
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        showFilters={viewMode === 'table'}
        onRefresh={handleRefresh}
        onExport={viewMode === 'table' ? exportToCSV : null}
        loading={loading}
        autoRefresh={autoRefresh}
        onAutoRefreshToggle={() => setAutoRefresh(prev => !prev)}
        lastUpdated={lastUpdated}
      />

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Content based on view mode */}
      {viewMode === 'charts' ? (
        /* Analytics Dashboard (charts only, no header) */
        <AnalyticsDashboard
          project={selectedProject}
          timeRange={timeRange}
          customRange={customRange}
          hideHeader={true}
        />
      ) : (
        /* Table View */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 text-left text-sm text-gray-600">
                  <th className="px-4 py-3 font-medium">Time</th>
                  <th className="px-4 py-3 font-medium">Project</th>
                  <th className="px-4 py-3 font-medium">Method</th>
                  <th className="px-4 py-3 font-medium">Path</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Location</th>
                  <th className="px-4 py-3 font-medium">Client</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      <ArrowPathIcon className="w-8 h-8 animate-spin mx-auto mb-2 text-gray-400" />
                      Loading traffic logs...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                      <GlobeAltIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      No traffic logs found
                      <p className="text-sm mt-1">Try adjusting your filters or time range</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => (
                    <TrafficRow key={`${log.timestamp}-${index}`} log={log} onClick={handleRowClick} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {logs.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
              Showing {logs.length} traffic entries
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      <TrafficDetailModal
        log={selectedLog}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedLog(null);
        }}
      />
    </div>
  );
}
